import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, TextField, Typography,
} from '@mui/material';
import { PosService } from '../../services/pos.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PosSettings } from '../../interfaces/pos.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';

// The screen holds strings for every text/number field and casts at submit -- same anatomy as
// ItemList.tsx and SalesSettings.tsx. Account ids are also plain strings ('' = none picked).
interface FormData {
    cashAccountId: string;
    revenueAccountId: string;
    taxPayableAccountId: string;
    roundingAccountId: string;
    discountAccountId: string;
    defaultTaxRatePct: string;
    pricesIncludeTax: boolean;
    allowNegativeStock: boolean;
    roundToNearest: string;
    receiptHeader: string;
    receiptFooter: string;
    receiptShowVatLine: boolean;
    catalogSyncIntervalSeconds: string;
}

const toFormData = (settings: PosSettings): FormData => ({
    cashAccountId: settings.cashAccountId ?? '',
    revenueAccountId: settings.revenueAccountId ?? '',
    taxPayableAccountId: settings.taxPayableAccountId ?? '',
    roundingAccountId: settings.roundingAccountId ?? '',
    discountAccountId: settings.discountAccountId ?? '',
    defaultTaxRatePct: settings.defaultTaxRatePct !== null && settings.defaultTaxRatePct !== undefined
        ? String(settings.defaultTaxRatePct) : '',
    pricesIncludeTax: settings.pricesIncludeTax ?? false,
    allowNegativeStock: settings.allowNegativeStock ?? true,
    roundToNearest: settings.roundToNearest !== null && settings.roundToNearest !== undefined
        ? String(settings.roundToNearest) : '',
    receiptHeader: settings.receiptHeader ?? '',
    receiptFooter: settings.receiptFooter ?? '',
    receiptShowVatLine: settings.receiptShowVatLine ?? false,
    catalogSyncIntervalSeconds: settings.catalogSyncIntervalSeconds !== null
        && settings.catalogSyncIntervalSeconds !== undefined
        ? String(settings.catalogSyncIntervalSeconds) : '',
});

const PosSettingsScreen: React.FC = () => {
    const toast = useToast();
    const { user } = useAuth();

    // id === null means "no settings row yet", NOT an error -- the backend always returns a
    // synthesized default DTO for an institution that has never configured POS. institutionId is
    // kept alongside so the id-less row can still be saved back with the right tenant.
    const [settings, setSettings] = useState<PosSettings | null>(null);
    const [formData, setFormData] = useState<FormData | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Write is narrower than read: InstitutionAdmin, StoreAdmin and Accountant can all view this
    // screen, but only InstitutionAdmin and Accountant may save. Default closed: an unresolved or
    // roleless user gets a read-only form, never an editable one that could 403 after they type.
    // Safe against the mount race too -- the spinner (loading || !formData || !settings) covers the
    // whole window before AuthContext's mount effect has had a chance to resolve `user`.
    const userRoles: string[] = user && user.roles
        ? (Array.isArray(user.roles) ? user.roles : [user.roles])
        : [];
    const canEdit = userRoles.includes('InstitutionAdmin') || userRoles.includes('Accountant');

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAll = async () => {
        try {
            setLoading(true);
            const [settingsRes, accountsRes] = await Promise.all([
                PosService.getSettings(),
                AccountService.getAllAccounts(),
            ]);
            setSettings(settingsRes);
            setFormData(toFormData(settingsRes));
            setAccounts(accountsRes ?? []);
        } catch (err) {
            // Deliberately does NOT touch settings/formData: on a failed *retry* this keeps whatever
            // was last successfully loaded out of the render path below rather than resurrecting it,
            // and on the *initial* load they are already null, which is exactly what routes render to
            // the error-with-retry state below instead of resurfacing the (now-stale) form.
            toast.showApiError(err, 'Failed to load POS settings');
        } finally {
            setLoading(false);
        }
    };

    const setField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
        setFormData(prev => (prev ? { ...prev, [key]: value } : prev));
    };

    const handleSave = async () => {
        if (!formData || !settings) return;
        const payload: PosSettings = {
            id: settings.id,
            institutionId: settings.institutionId ?? AuthService.getSelectedInstitution() ?? '',
            cashAccountId: formData.cashAccountId || null,
            revenueAccountId: formData.revenueAccountId || null,
            taxPayableAccountId: formData.taxPayableAccountId || null,
            roundingAccountId: formData.roundingAccountId || null,
            discountAccountId: formData.discountAccountId || null,
            defaultTaxRatePct: formData.defaultTaxRatePct === '' ? null : Number(formData.defaultTaxRatePct),
            pricesIncludeTax: formData.pricesIncludeTax,
            allowNegativeStock: formData.allowNegativeStock,
            roundToNearest: formData.roundToNearest === '' ? null : Number(formData.roundToNearest),
            receiptHeader: formData.receiptHeader === '' ? null : formData.receiptHeader,
            receiptFooter: formData.receiptFooter === '' ? null : formData.receiptFooter,
            receiptShowVatLine: formData.receiptShowVatLine,
            catalogSyncIntervalSeconds: formData.catalogSyncIntervalSeconds === ''
                ? null : Number(formData.catalogSyncIntervalSeconds),
        };
        try {
            setSaving(true);
            const saved = await PosService.saveSettings(payload);
            setSettings(saved);
            setFormData(toFormData(saved));
            toast.showSuccess('POS settings saved');
        } catch (err) {
            toast.showApiError(err, 'Failed to save POS settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
    }

    // A failed GET (403 for a role the backend doesn't admit, 500, offline, ...) must not leave the
    // screen spinning forever with nothing but a toast that has already faded -- give the user a way
    // back. `formData`/`settings` are only ever set together on a successful load, and a failed
    // *save* (handled separately, inline via toast) never touches either, so this is a reliable
    // "did the load succeed" check with no separate error flag needed.
    if (!formData || !settings) {
        return (
            <Box sx={{ p: 3, maxWidth: 720 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load POS settings.
                </Alert>
                <Button variant="outlined" onClick={loadAll}>Retry</Button>
            </Box>
        );
    }

    // Derived purely client-side -- there is no backend flag for "is POS configured". A sale is
    // never rejected for a misconfigured chart of accounts: it is held at PENDING/FAILED until
    // every one of these five accounts is set, then cleared from the unposted queue.
    const accountingUnconfigured =
        !settings.cashAccountId || !settings.revenueAccountId || !settings.taxPayableAccountId
        || !settings.roundingAccountId || !settings.discountAccountId;

    const assetAccounts = accounts.filter(a => a.accountType === 'ASSET');
    const revenueAccounts = accounts.filter(a => a.accountType === 'REVENUE');
    const liabilityAccounts = accounts.filter(a => a.accountType === 'LIABILITY');
    const revenueOrExpenseAccounts = accounts.filter(a => a.accountType === 'REVENUE' || a.accountType === 'EXPENSE');

    return (
        <Box sx={{ p: 3, maxWidth: 720 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>POS Settings</Typography>

            {accountingUnconfigured && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    POS accounting is not configured. Sales will still be rung and will never be lost, but
                    each one is held with posting status PENDING or FAILED until every account below is set.
                    Set them, then use Post now on the unposted queue to clear the backlog.
                </Alert>
            )}

            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <AccountAutocomplete
                    label="Cash Account"
                    accounts={assetAccounts}
                    value={formData.cashAccountId}
                    onChange={id => setField('cashAccountId', id)}
                    disabled={!canEdit}
                    placeholder="None"
                />
                <AccountAutocomplete
                    label="Revenue Account"
                    accounts={revenueAccounts}
                    value={formData.revenueAccountId}
                    onChange={id => setField('revenueAccountId', id)}
                    disabled={!canEdit}
                    placeholder="None"
                />
                <AccountAutocomplete
                    label="Tax Payable Account"
                    accounts={liabilityAccounts}
                    value={formData.taxPayableAccountId}
                    onChange={id => setField('taxPayableAccountId', id)}
                    disabled={!canEdit}
                    placeholder="None"
                />
                <AccountAutocomplete
                    label="Rounding Account"
                    accounts={revenueOrExpenseAccounts}
                    value={formData.roundingAccountId}
                    onChange={id => setField('roundingAccountId', id)}
                    disabled={!canEdit}
                    placeholder="None"
                />
                <AccountAutocomplete
                    label="Discount Account"
                    accounts={revenueOrExpenseAccounts}
                    value={formData.discountAccountId}
                    onChange={id => setField('discountAccountId', id)}
                    disabled={!canEdit}
                    placeholder="None"
                />

                <TextField
                    label="Default Tax Rate (%)"
                    type="number"
                    value={formData.defaultTaxRatePct}
                    onChange={e => setField('defaultTaxRatePct', e.target.value)}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    disabled={!canEdit}
                    fullWidth
                />
                <TextField
                    label="Round To Nearest"
                    type="number"
                    value={formData.roundToNearest}
                    onChange={e => setField('roundToNearest', e.target.value)}
                    inputProps={{ min: 0, step: 0.0001 }}
                    disabled={!canEdit}
                    fullWidth
                />
                <TextField
                    label="Catalog Sync Interval (seconds)"
                    type="number"
                    value={formData.catalogSyncIntervalSeconds}
                    onChange={e => setField('catalogSyncIntervalSeconds', e.target.value)}
                    inputProps={{ min: 30, max: 86400, step: 1 }}
                    disabled={!canEdit}
                    fullWidth
                />

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formData.pricesIncludeTax}
                            onChange={e => setField('pricesIncludeTax', e.target.checked)}
                            disabled={!canEdit}
                        />
                    }
                    label="Prices Include Tax"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formData.allowNegativeStock}
                            onChange={e => setField('allowNegativeStock', e.target.checked)}
                            disabled={!canEdit}
                        />
                    }
                    label="Allow Negative Stock"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formData.receiptShowVatLine}
                            onChange={e => setField('receiptShowVatLine', e.target.checked)}
                            disabled={!canEdit}
                        />
                    }
                    label="Show VAT Line On Receipt"
                />

                <TextField
                    label="Receipt Header"
                    value={formData.receiptHeader}
                    onChange={e => setField('receiptHeader', e.target.value)}
                    disabled={!canEdit}
                    multiline
                    rows={2}
                    fullWidth
                />
                <TextField
                    label="Receipt Footer"
                    value={formData.receiptFooter}
                    onChange={e => setField('receiptFooter', e.target.value)}
                    disabled={!canEdit}
                    multiline
                    rows={2}
                    fullWidth
                />

                {canEdit && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default PosSettingsScreen;
